import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, Clock, CheckCircle2, Loader2, XCircle } from "lucide-react";

const STAGES = [
  ["paid", "Payment confirmed"],
  ["queued", "Queued"],
  ["calculating", "Calculating numbers"],
  ["generating_report", "Writing your report"],
  ["validating_pdf", "Preparing PDF"],
  ["scheduled_for_delivery", "Scheduled for delivery"],
  ["delivered", "Delivered"],
];
const ORDER = STAGES.map((s) => s[0]);

export default function TrackOrder() {
  const nav = useNavigate();
  const [orderId, setOrderId] = useState("");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    e?.preventDefault();
    if (!orderId.trim()) return;
    setLoading(true); setStatus(null);
    try {
      const { data } = await api.get(`/orders/${orderId.trim()}/status`);
      setStatus(data);
    } catch (err) { setStatus({ error: apiError(err) }); }
    finally { setLoading(false); }
  };

  const idx = status && !status.error ? ORDER.indexOf(status.status) : -1;
  const failed = status && ["generation_failed", "delivery_failed", "cancelled", "refunded"].includes(status.status);

  return (
    <>
      <Seo title="Track Your Order" path="/track-order" />
      <PageHeader eyebrow="Order status" title="Track your report"
        subtitle="Enter your Order ID (AET-…) from your confirmation email to see where your report is in our process." />
      <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-12 py-16 lg:py-20">
        <form onSubmit={lookup} className="flex gap-3" data-testid="track-order-form">
          <Input data-testid="track-order-input" value={orderId} onChange={(e) => setOrderId(e.target.value)}
            placeholder="AET-XXXXXXXX" aria-label="Order ID" className="font-mono-accent" />
          <button type="submit" className="btn-primary" data-testid="track-order-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Track
          </button>
        </form>

        {status?.error && (
          <div data-testid="track-order-error" className="mt-8 bg-white border border-rose-200 rounded-xl p-6 text-rose-700 flex items-center gap-2">
            <XCircle className="w-5 h-5" /> {status.error}
          </div>
        )}

        {status && !status.error && (
          <div data-testid="track-order-result" className="mt-8 bg-white border border-border rounded-2xl p-8">
            <div className="flex justify-between items-start mb-6">
              <div>
                <p className="eyebrow mb-1">{status.package_name}</p>
                <h3 className="font-serif text-2xl text-navy">{status.customer_name}</h3>
                <p className="text-xs text-slate-400 font-mono-accent mt-1">{status.order_id}</p>
              </div>
              <span className="text-sm font-medium text-gold-hover">₹{status.amount}</span>
            </div>

            {failed ? (
              <div className="bg-paper rounded-xl p-5 text-slate-700">
                Current status: <strong>{status.status.replace(/_/g, " ")}</strong>. If you need help,
                please contact support with your Order ID.
              </div>
            ) : (
              <ol className="space-y-3">
                {STAGES.map(([key, label], i) => {
                  const done = idx >= 0 && i <= idx;
                  const current = i === idx;
                  return (
                    <li key={key} className="flex items-center gap-3">
                      {done ? <CheckCircle2 className="w-5 h-5 text-gold-hover" />
                        : <div className="w-5 h-5 rounded-full border-2 border-slate-200" />}
                      <span className={`text-sm ${current ? "font-semibold text-navy" : done ? "text-slate-700" : "text-slate-400"}`}>
                        {label}
                      </span>
                    </li>
                  );
                })}
              </ol>
            )}

            {status.status === "delivered" && (
              <p className="mt-6 text-sm text-slate-600 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-gold-hover" /> Your report has been emailed. Check your inbox (and spam) for the download link.
              </p>
            )}
            {status.scheduled_delivery_at && status.status !== "delivered" && (
              <p className="mt-6 text-xs text-slate-500 flex items-center gap-2">
                <Clock className="w-3.5 h-3.5" /> Estimated delivery around {new Date(status.scheduled_delivery_at).toLocaleString()}
              </p>
            )}
          </div>
        )}
        <p className="text-center mt-8 text-sm text-slate-500">
          Don't have an order yet? <button onClick={() => nav("/pricing")} className="text-gold-hover underline">Choose a report</button>
        </p>
      </section>
    </>
  );
}
